import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const UserManagement = () => {
    const { user: currentUser } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'roles'
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modals state
    const [showUserModal, setShowUserModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editingRole, setEditingRole] = useState(null);

    // Form states
    const [userFormData, setUserFormData] = useState({
        name: '',
        email: '',
        password: '',
        roleId: '',
        status: '',
        avatar: '',
        warehouseIds: []
    });


    const [roleFormData, setRoleFormData] = useState({
        name: '',
        description: '',
        permissionIds: []
    });

    const fetchData = useCallback(async () => {
        if (!currentUser?.companyId) return;
        setLoading(true);
        try {
            const [usersRes, rolesRes, permsAllRes, warehousesRes] = await Promise.all([
                fetch(`/api/users/${currentUser.companyId}`),
                fetch('/api/roles'),
                fetch('/api/roles/permissions/all'),
                fetch(`/api/warehouses?companyId=${currentUser.companyId}`)
            ]);

            if (usersRes.ok && rolesRes.ok && permsAllRes.ok) {
                const usersData = await usersRes.json();
                const rolesData = await rolesRes.json();
                const permsData = await permsAllRes.json();
                const warehousesData = warehousesRes.ok ? await warehousesRes.json() : [];
                setUsers(usersData);
                setRoles(rolesData);
                setAllPermissions(permsData);
                setWarehouses(warehousesData);
            }
        } catch (err) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, [currentUser, showToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // User Handlers
    const handleOpenUserModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setUserFormData({
                name: user.name,
                email: user.email,
                password: '',
                roleId: user.roleId,
                status: user.status,
                avatar: user.avatar || '',
                warehouseIds: user.warehouses ? user.warehouses.map(w => w.id) : []
            });
        } else {
            setEditingUser(null);
            setUserFormData({
                name: '',
                email: '',
                password: '',
                roleId: '',
                status: '',
                avatar: '',
                warehouseIds: []
            });

        }
        setShowUserModal(true);
    };

    const handleUserSubmit = async (e) => {
        e.preventDefault();
        const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
        const method = editingUser ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...userFormData,
                    companyId: currentUser.companyId
                })
            });

            if (response.ok) {
                showToast(editingUser ? "Utilisateur mis à jour" : "Utilisateur créé", "success");
                setShowUserModal(false);
                fetchData();
            } else {
                const data = await response.json();
                showToast(data.error || "Une erreur est survenue", "error");
            }
        } catch (err) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleUserDelete = (id) => {
        showConfirm({
            title: "Supprimer l'utilisateur",
            message: "Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.",
            type: "danger",
            confirmText: "Supprimer",
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/users/${id}`, { method: 'DELETE' });
                    if (response.ok) {
                        showToast("Utilisateur supprimé", "success");
                        fetchData();
                    } else {
                        const data = await response.json();
                        showToast(data.error || "Erreur lors de la suppression", "error");
                    }
                } catch (err) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    // Role Handlers
    const handleOpenRoleModal = (role = null) => {
        if (role) {
            setEditingRole(role);
            setRoleFormData({
                name: role.name,
                description: role.description || '',
                permissionIds: role.permissions?.map(p => p.id) || []
            });
        } else {
            setEditingRole(null);
            setRoleFormData({
                name: '',
                description: '',
                permissionIds: []
            });
        }
        setShowRoleModal(true);
    };

    const handleRoleSubmit = async (e) => {
        e.preventDefault();
        const url = editingRole ? `/api/roles/${editingRole.id}` : '/api/roles';
        const method = editingRole ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(roleFormData)
            });

            if (response.ok) {
                showToast(editingRole ? "Rôle mis à jour" : "Rôle créé", "success");
                setShowRoleModal(false);
                fetchData();
            } else {
                const data = await response.json();
                showToast(data.error || "Erreur lors de l'enregistrement", "error");
            }
        } catch (err) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleRoleDelete = (id) => {
        showConfirm({
            title: "Supprimer le rôle",
            message: "Supprimer ce rôle ? Cela pourrait affecter les utilisateurs associés.",
            type: "danger",
            confirmText: "Supprimer",
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/roles/${id}`, { method: 'DELETE' });
                    if (response.ok) {
                        showToast("Rôle supprimé", "success");
                        fetchData();
                    } else {
                        const data = await response.json();
                        showToast(data.error || "Erreur", "error");
                    }
                } catch (err) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    const togglePermission = (id) => {
        setRoleFormData(prev => ({
            ...prev,
            permissionIds: prev.permissionIds.includes(id)
                ? prev.permissionIds.filter(pid => pid !== id)
                : [...prev.permissionIds, id]
        }));
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Page Header - Sticky */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-wrap justify-between items-end gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-col gap-1">
                    <h1 className="text-3xl font-black leading-tight tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Gestion des Utilisateurs</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-base">Gerez les accès et les permissions de votre équipe logistique</p>
                </div>
                <button
                    onClick={() => activeTab === 'users' ? handleOpenUserModal() : handleOpenRoleModal()}
                    className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all shadow-lg shadow-primary/20"
                >
                    <span className="material-symbols-outlined">{activeTab === 'users' ? 'person_add' : 'shield_person'}</span>
                    <span>{activeTab === 'users' ? 'Ajouter un utilisateur' : 'Ajouter un rôle'}</span>
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200 dark:border-slate-800 gap-8">
                <button
                    onClick={() => setActiveTab('users')}
                    className={`border-b-2 pb-3 pt-2 text-sm font-bold transition-all ${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    Liste des utilisateurs
                </button>
                <button
                    onClick={() => setActiveTab('roles')}
                    className={`border-b-2 pb-3 pt-2 text-sm font-bold transition-all ${activeTab === 'roles' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    Rôles & Permissions
                </button>
            </div>

            {activeTab === 'users' ? (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {/* User Table Section */}
                    <div className="xl:col-span-2 space-y-4">
                        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-800/50">
                                            <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Utilisateur</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">E-mail</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Rôle</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Entrepôts</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Statut</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {loading ? (
                                            <tr><td colSpan="5" className="px-6 py-10 text-center text-slate-400 italic">Chargement...</td></tr>
                                        ) : users.length === 0 ? (
                                            <tr><td colSpan="5" className="px-6 py-10 text-center text-slate-400 italic">Aucun utilisateur trouvé</td></tr>
                                        ) : users.map(user => (
                                            <UserRow
                                                key={user.id}
                                                user={user}
                                                onEdit={() => handleOpenUserModal(user)}
                                                onDelete={() => handleUserDelete(user.id)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Permissions Summary Section */}
                    <div className="flex flex-col gap-6">
                        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Aperçu des Rôles</h3>
                                <span className="material-symbols-outlined text-primary">security</span>
                            </div>
                            <div className="space-y-6">
                                {roles.map(role => (
                                    <RoleRowSimple key={role.id} role={role} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Nom du Rôle</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Description</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider">Permissions</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase text-slate-400 tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {loading ? (
                                        <tr><td colSpan="4" className="px-6 py-10 text-center text-slate-400 italic">Chargement...</td></tr>
                                    ) : roles.map(role => (
                                        <tr key={role.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                            <td className="px-6 py-4">
                                                <span className="font-bold text-slate-900 dark:text-white">{role.name}</span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">{role.description}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {role.permissions?.map(p => (
                                                        <span key={p.id} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-[10px] font-bold text-slate-600 rounded" title={p.description}>
                                                            {p.label || p.name}
                                                        </span>
                                                    ))}
                                                    {(!role.permissions || role.permissions.length === 0) && <span className="text-xs text-slate-400 italic">Aucune</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => handleOpenRoleModal(role)} className="p-2 text-slate-400 hover:text-primary transition-colors"><span className="material-symbols-outlined text-lg">edit</span></button>
                                                    {role.name !== 'Admin' && (
                                                        <button onClick={() => handleRoleDelete(role.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><span className="material-symbols-outlined text-lg">delete</span></button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showUserModal && (
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 border border-slate-200 dark:border-slate-800 w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-500">
                        {/* Premium Header with Gradient */}
                        <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                            <div className="relative z-10 flex justify-between items-center">
                                <div className="flex gap-4 items-center">
                                    <div className="size-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-primary/10">
                                        <span className="material-symbols-outlined text-primary text-[28px] font-bold">{editingUser ? 'manage_accounts' : 'person_add'}</span>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                            {editingUser ? "Éditer Profil" : "Nouvel Utilisateur"}
                                        </h3>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">
                                            {editingUser ? editingUser.email : 'Enregistrement nouveau membre'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowUserModal(false)}
                                    className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleUserSubmit} className="p-8 space-y-6">
                            <div className="grid grid-cols-1 gap-5">
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Nom complet</label>
                                    <div className="relative group">
                                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-lg">badge</span>
                                        <input
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all text-sm font-black text-slate-900 dark:text-white"
                                            placeholder="Ex: Jean Dupont"
                                            value={userFormData.name}
                                            onChange={e => setUserFormData({ ...userFormData, name: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Email professionnel</label>
                                    <div className="relative group">
                                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-lg">mail</span>
                                        <input
                                            required
                                            type="email"
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all text-sm font-black text-slate-900 dark:text-white"
                                            placeholder="email@exemple.com"
                                            value={userFormData.email}
                                            onChange={e => setUserFormData({ ...userFormData, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Mot de passe {editingUser && '(optionnel)'}</label>
                                    <div className="relative group">
                                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-lg">lock</span>
                                        <input
                                            required={!editingUser}
                                            type="password"
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all text-sm font-black text-slate-900 dark:text-white"
                                            placeholder={editingUser ? "Inchangé si vide" : "••••••••"}
                                            value={userFormData.password}
                                            onChange={e => setUserFormData({ ...userFormData, password: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Rôle</label>
                                        <select
                                            required
                                            className="w-full h-14 px-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl focus:border-primary/30 focus:ring-4 focus:ring-primary/5 outline-none transition-all text-sm font-black text-slate-900 dark:text-white"
                                            value={userFormData.roleId}
                                            onChange={e => setUserFormData({ ...userFormData, roleId: e.target.value })}
                                        >
                                            <option value="">Sélectionner</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Statut</label>
                                        <select
                                            required
                                            className="w-full h-14 px-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl focus:border-primary/30 focus:ring-4 focus:ring-primary/5 outline-none transition-all text-sm font-black text-slate-900 dark:text-white"
                                            value={userFormData.status}
                                            onChange={e => setUserFormData({ ...userFormData, status: e.target.value })}
                                        >
                                            <option value="">Sélectionner</option>
                                            <option value="Actif">Actif</option>
                                            <option value="Inactif">Inactif</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Choix des entrepôts */}
                            <div className="flex flex-col gap-3 pt-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Accès Entrepôts</label>
                                <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] border border-slate-100 dark:border-slate-800 max-h-[160px] overflow-y-auto">
                                    {warehouses.length > 0 ? warehouses.map(w => (
                                        <label key={w.id} className="flex items-center gap-3 p-3 rounded-xl border border-transparent hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm transition-all cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                className="size-4 rounded border-slate-300 text-primary focus:ring-primary"
                                                checked={userFormData.warehouseIds.includes(w.id)}
                                                onChange={(e) => {
                                                    const current = userFormData.warehouseIds;
                                                    setUserFormData({
                                                        ...userFormData,
                                                        warehouseIds: e.target.checked ? [...current, w.id] : current.filter(id => id !== w.id)
                                                    });
                                                }}
                                            />
                                            <span className="text-xs font-black text-slate-700 dark:text-slate-300 group-hover:text-primary transition-colors">{w.name}</span>
                                        </label>
                                    )) : (
                                        <div className="col-span-2 text-xs text-slate-400 italic font-medium p-4 text-center">Aucun entrepôt disponible.</div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowUserModal(false)} className="flex-1 py-4 text-[11px] font-black uppercase tracking-widest border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all">Annuler</button>
                                <button type="submit" className="flex-1 py-4 text-[11px] font-black uppercase tracking-widest bg-primary text-white rounded-2xl shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all transform active:scale-95">{editingUser ? "Enregistrer" : "Créer l'accès"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Role Form Modal Premium */}
            {showRoleModal && (
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-purple-500/10 border border-slate-200 dark:border-slate-800 w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-500">
                        {/* Premium Header Purple */}
                        <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-purple-500/10 via-purple-500/5 to-transparent dark:from-purple-500/20 dark:via-purple-500/10 dark:to-transparent border-b border-purple-500/10 dark:border-purple-500/20">
                            <div className="relative z-10 flex justify-between items-center">
                                <div className="flex gap-4 items-center">
                                    <div className="size-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-purple-500/10">
                                        <span className="material-symbols-outlined text-purple-500 text-[28px] font-bold">shield_person</span>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                            {editingRole ? "Éditer le Rôle" : "Nouveau Rôle"}
                                        </h3>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">Configuration des privilèges système</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowRoleModal(false)}
                                    className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleRoleSubmit} className="p-8 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Nom du rôle</label>
                                    <div className="relative group">
                                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-500 transition-colors text-lg">label</span>
                                        <input
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-purple-500/30 focus:ring-4 focus:ring-purple-500/5 transition-all text-sm font-black text-slate-900 dark:text-white"
                                            placeholder="Ex: Manager Logistique"
                                            value={roleFormData.name}
                                            onChange={e => setRoleFormData({ ...roleFormData, name: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Description courte</label>
                                    <div className="relative group">
                                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-500 transition-colors text-lg">info</span>
                                        <input
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-purple-500/30 focus:ring-4 focus:ring-purple-500/5 transition-all text-sm font-black text-slate-900 dark:text-white"
                                            placeholder="Rôle de supervision..."
                                            value={roleFormData.description}
                                            onChange={e => setRoleFormData({ ...roleFormData, description: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-1 h-4 bg-purple-500 rounded-full"></div>
                                    <h3 className="font-black text-[10px] uppercase tracking-widest text-slate-400">Permissions Granulaires</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border border-slate-100 dark:border-slate-800 max-h-[300px] overflow-y-auto">
                                    {allPermissions.map(perm => (
                                        <label key={perm.id} className="flex items-start gap-4 p-4 rounded-2xl border border-transparent hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm transition-all cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                className="mt-1 size-5 rounded-lg border-slate-300 text-purple-600 focus:ring-purple-500"
                                                checked={roleFormData.permissionIds.includes(perm.id)}
                                                onChange={() => togglePermission(perm.id)}
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-black text-slate-700 dark:text-slate-300 group-hover:text-purple-600 transition-colors">{perm.label || perm.name}</span>
                                                <span className="text-[10px] text-slate-500 font-medium leading-tight mt-0.5">{perm.description}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <button type="button" onClick={() => setShowRoleModal(false)} className="flex-1 py-4 text-[11px] font-black uppercase tracking-widest border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all">Annuler</button>
                                <button type="submit" className="flex-1 py-4 text-[11px] font-black uppercase tracking-widest bg-purple-600 text-white rounded-2xl shadow-xl shadow-purple-500/20 hover:bg-purple-700 transition-all transform active:scale-95">{editingRole ? "Enregistrer" : "Créer le rôle"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Sub-components
const UserRow = ({ user, onEdit, onDelete }) => (
    <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors cursor-default">
        <td className="px-6 py-4">
            <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 overflow-hidden text-primary font-bold">
                    {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" /> : user.name.charAt(0)}
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{user.name}</span>
            </div>
        </td>
        <td className="px-6 py-4 text-slate-600 dark:text-slate-400 text-sm">{user.email}</td>
        <td className="px-6 py-4">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${user.role?.name === 'Admin' ? 'bg-primary/10 text-primary' : 'bg-slate-100 dark:bg-slate-800 text-slate-700'}`}>
                {user.role?.name || 'Inconnu'}
            </span>
        </td>
        <td className="px-6 py-4">
            {user.warehouses && user.warehouses.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                    {user.warehouses.slice(0, 2).map(w => (
                        <span key={w.id} className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 px-2 py-0.5 rounded font-medium border border-slate-200 dark:border-slate-700">
                            {w.name}
                        </span>
                    ))}
                    {user.warehouses.length > 2 && (
                        <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded font-medium border border-slate-200 dark:border-slate-700">
                            +{user.warehouses.length - 2}
                        </span>
                    )}
                </div>
            ) : (
                <span className="text-xs text-slate-400 italic">Tous / Aucun</span>
            )}
        </td>
        <td className="px-6 py-4">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${user.status === 'Actif' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                <span className={`size-1.5 rounded-full ${user.status === 'Actif' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                {user.status}
            </span>
        </td>
        <td className="px-6 py-4 text-right">
            <div className="flex justify-end gap-2">
                <button onClick={onEdit} className="p-2 text-slate-400 hover:text-primary transition-colors"><span className="material-symbols-outlined text-lg">edit</span></button>
                <button onClick={onDelete} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><span className="material-symbols-outlined text-lg">delete</span></button>
            </div>
        </td>
    </tr>
);

const RoleRowSimple = ({ role }) => (
    <div className="pt-4 first:pt-0 border-t first:border-0 border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{role.name}</span>
            {role.name === 'Admin' && <span className="text-[10px] font-black uppercase text-primary bg-primary/10 px-2 py-0.5 rounded">Accès Total</span>}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 italic leading-relaxed">
            {role.description || "Aucune description."}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
            {role.permissions?.slice(0, 3).map(p => (
                <span key={p.id} className="text-[9px] bg-slate-50 dark:bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-100 dark:border-slate-800">
                    {p.label || p.name}
                </span>
            ))}
            {role.permissions?.length > 3 && <span className="text-[9px] text-slate-400">+ {role.permissions.length - 3}</span>}
        </div>
    </div>
);

export default UserManagement;
